/* eslint-disable @typescript-eslint/naming-convention */
import React, { FC } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { FacebookShareButton, TwitterShareButton, TwitterIcon, FacebookIcon, LinkedinIcon, LinkedinShareButton } from 'next-share';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Post } from 'components/modules/Blog/SingleCard';
import { PageWithLayoutType } from 'components/organisms/layouts';
import { Layout } from 'components/organisms/layouts/Layout';
import { env } from 'env/client.mjs';
import { SubscribeInputBlock } from 'components/modules/Blog/SubscribeInput';
const SingleCard = dynamic<Post>(() => import('components/modules/Blog/SingleCard').then(module => module.SingleCard));
const PAGES_WITH_SHOWN_CHARGERS = ['level-2-how-to-choose-a-charger-how-many-amps-could-my-home-ev', 'how-much-power-do-homeowners-need-for-their-ev-charger'];
const SVG = () => {
  return <svg style={{
    width: 12,
    // Equivalent to w-3 in Tailwind
    height: 12,
    // Equivalent to h-3 in Tailwind
    stroke: '#CBD5E0',
    // Equivalent to text-gray-400 in Tailwind
    marginLeft: 4,
    // Equivalent to mx-1 in Tailwind
    marginRight: 4 // Equivalent to mx-1 in Tailwind
  }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
    </svg>;
};
const extractHeadings = (htmlContent: any) => {
  const headingRegex = /<(h[1-6])[^>]*>(.*?)<\/\1>/g;
  const matches = [];
  let match;
  while (match = headingRegex.exec(htmlContent)) {
    // @ts-ignore
    const idMatch = match[0]?.match(/id="([^"]*)"/) as any;
    matches.push({
      text: match[2],
      id: idMatch[1]
    });
  }
  return matches;
};
const stripHtmlTags = (htmlString: string) => {
  if (typeof window !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
};
const Home: FC<{
  post: Post;
  suggestedPosts: Array<Post>;
}> = ({
  post = {} as Post,
  suggestedPosts = [] as Array<Post>
}) => {
  const {
    isFallback,
    asPath,
    pathname
  } = useRouter();
  if (isFallback) return <Box>Loading...</Box>;
  const chargersShown = PAGES_WITH_SHOWN_CHARGERS.includes(post?.slug);
  const links = extractHeadings(post?.html);
  const dateObj = new Date(post?.published_at);

  // Format the date using toLocaleDateString
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <>
      <Head>
        <title>{post?.title} | Lectrium</title>
        <meta property="og:title" content={post?.title} />
        <meta property="og:description" content={post?.excerpt} />
        <meta name="description" content={post?.excerpt} />
        <meta property="og:url" content={`https://lectrium.com${pathname}`} />
        <link rel="canonical" href={`https://www.lectrium.com${pathname}`} />
        <meta property="og:type" content="website" />
        <link rel="icon" href="/favicon.png" />
        <meta name="viewport" content="width=device-width, viewport-fit=cover" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:locale" content="en_GB" />
        <title>{post?.title} | Lectrium</title>
        <meta property="og:image" content={post?.feature_image} />
        <meta name="twitter:image" content={post?.feature_image} />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <Box>
        <Box sx={{
        px: {
          xs: '5%',
          md: '20%'
        },
        py: '2%'
      }}>
          <nav aria-label="Breadcrumb">
            <Box sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            listStyle: 'none',
            padding: 0
          }}>
              <Box sx={{
              display: 'inline-flex',
              alignItems: 'center'
            }}>
                <SVG />
                <Link href="/" style={{
                display: 'inline-flex',
                alignItems: 'left',
                fontSize: '0.875rem',
                // Closest to Tailwind's text-sm
                fontWeight: 500,
                // Closest to Tailwind's font-medium
                color: '#4A5568',
                // Equivalent to text-gray-700 in Tailwind
                textDecoration: 'none',
                fontFamily: 'Gilroy'
              }}>
                  Home
                </Link>
              </Box>

              <Box sx={{
              display: 'inline-flex',
              alignItems: 'center'
            }}>
                <SVG />
                <Link href="/blog" style={{
                display: 'inline-flex',
                alignItems: 'left',
                fontFamily: 'Gilroy',
                fontSize: '0.875rem',
                // Closest to Tailwind's text-sm
                fontWeight: 500,
                // Closest to Tailwind's font-medium
                color: '#4A5568',
                // Equivalent to text-gray-700 in Tailwind
                textDecoration: 'none'
              }}>
                  Blog
                </Link>
              </Box>

              {post?.tags && <li style={{
              padding: 0,
              lineHeight: '1rem'
            }}>
                  <Box sx={{
                display: 'inline-flex',
                alignItems: 'center'
              }}>
                    <SVG />
                    <Link href={`/blog/category/${post?.tags[0]?.slug}`} style={{
                  display: 'inline-flex',
                  alignItems: 'left',
                  fontFamily: 'Gilroy',
                  fontSize: '0.875rem',
                  // Closest to Tailwind's text-sm
                  fontWeight: 500,
                  // Closest to Tailwind's font-medium
                  color: '#4A5568',
                  // Equivalent to text-gray-700 in Tailwind
                  textDecoration: 'none'
                }}>
                      {post?.tags[0]?.name}
                    </Link>
                  </Box>
                </li>}

              <li style={{
              padding: 0,
              lineHeight: '1rem'
            }}>
                <Box sx={{
                display: 'inline-flex',
                alignItems: 'center'
              }}>
                  <SVG />

                  <span style={{
                  display: 'inline-flex',
                  alignItems: 'left',
                  fontFamily: 'Gilroy',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#4A5568',
                  textDecoration: 'none'
                }}>
                    {post?.title}
                  </span>
                </Box>
              </li>
            </Box>
          </nav>
          <Box sx={{
          mt: '1rem'
        }}>
            <Typography sx={{
            fontFamily: 'Gilroy'
          }}>
              <span style={{
              fontWeight: 500,
              fontFamily: 'Gilroy'
            }}>Published date: </span>
              {formattedDate}
            </Typography>
          </Box>
        </Box>
        <Grid container sx={{
        borderBottom: '1px solid #E4ECF4'
      }}>
          <Grid item xs={12} sx={{
          textAlign: 'center',
          display: 'block'
        }}>
            <Box sx={{
            px: {
              xs: '5%',
              md: '20%'
            },
            pb: '5%'
          }}>
              <Typography variant="h2" component="h1" textAlign="left" fontWeight={700} sx={{
              display: {
                xs: 'none',
                md: 'block'
              },
              fontFamily: 'Gilroy'
            }}>
                {post?.title}
              </Typography>
              <Typography variant="h3" component="h1" textAlign="left" fontWeight={700} sx={{
              display: {
                xs: 'block',
                md: 'none'
              },
              fontFamily: 'Gilroy'
            }}>
                {post?.title}
              </Typography>
              <Typography pt="0.5rem" textAlign="left" style={{
              fontFamily: 'Gilroy'
            }}>
                {post?.excerpt}
              </Typography>
            </Box>

            <Image src={post?.feature_image} alt={post?.feature_image_alt} height={800} width={800} style={{
            borderRadius: '6px',
            margin: '0 auto 5% auto',
            maxWidth: '85%',
            maxHeight: '800px'
          }} />

            <Box sx={{
            border: '1px solid rgb(228,236,244)',
            maxWidth: '450px',
            width: '100%',
            m: '0 auto',
            borderRadius: '1rem',
            px: '1rem'
          }}>
              <Typography variant="h5" sx={{
              mt: '1rem',
              textAlign: 'center',
              fontWeight: 600
            }}>
                Table of contents
              </Typography>
              {links.map((item, i) => <Box key={i}>
                  <Link style={{
                marginTop: '1rem',
                marginBottom: '1rem',
                display: 'flex',
                flexDirection: 'row',
                fontFamily: 'Gilroy',
                textAlign: 'left',
                color: 'black'
              }} href={`/blog/${post?.slug}#${item.id}`}>
                    {i + 1}.&nbsp;
                    {stripHtmlTags(item?.text as any)}
                  </Link>
                </Box>)}
            </Box>

            <Box sx={{
            textAlign: 'left',
            px: {
              md: '20%'
            },
            py: '5%'
          }}>
              <section className="gh-content gh-canvas" dangerouslySetInnerHTML={{
              __html: post?.html
            }} style={{
              fontFamily: 'Gilroy'
            }} />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            mb: '5%',
            mx: {
              xs: '1rem',
              md: 'auto'
            },
            border: '1px solid #E4ECF4',
            borderRadius: '0.5rem',
            p: '2rem',
            maxWidth: '800px',
            textAlign: 'center'
          }}>
              <Typography variant="h5" sx={{
              fontWeight: 700,
              mb: '1rem'
            }}>
                Lectrium upgrades dealership EV detail pages with impactful data and visuals for shoppers considering
                electric.
              </Typography>
              <Typography variant="body1" sx={{
              mb: '1rem'
            }}>
                <Link href="/ev-savings-badge" passHref style={{
                color: '#3547C5',
                fontWeight: 600
              }} target="_blank">
                  The EV Savings Badge:
                </Link>{' '}
                Join 300+ dealerships across the U.S. showcasing EV ownership savings on their website. Highlight
                federal and state tax credits, fuel cost savings, and maintenance savings for each EV and PHEV in your
                inventory.
              </Typography>
              <Typography variant="body1" sx={{
              mb: '1rem'
            }}>
                <Link href="/ev-range-map" passHref style={{
                color: '#3547C5',
                fontWeight: 600
              }} target="_blank">
                  The EV Range Map:
                </Link>{' '}
                Join 200+ dealerships addressing range anxiety by helping their customers visualize the range of each EV
                and PHEV inventory on an interactive map.
              </Typography>
              <Typography variant="body1" sx={{
              mb: '1rem'
            }}>
                <Link href="/co2-savings-badge" passHref style={{
                color: '#3547C5',
                fontWeight: 600
              }} target="_blank">
                  The CO2 Savings Badge:
                </Link>{' '}
                Our newest tool showcases the environmental impact of your electric inventory, providing
                easy-to-understand CO2 emissions savings data.
              </Typography>
              <Typography variant="body1" sx={{
              mb: '1rem'
            }}>
                Enable shoppers to make informed purchasing decisions, and emphasize the value of your EV and PHEV
                inventory by bundling these tools on your website. Book a meeting with our team to get a full demo.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <SubscribeInputBlock />

        <Box sx={{
        py: '3rem',
        textAlign: 'left',
        width: {
          xs: '90%',
          sm: '80%'
        },
        m: '0 auto',
        display: chargersShown ? 'none' : 'block'
      }}>
          <Grid container spacing={5}>
            {suggestedPosts?.map((singlePost, index) =>
          // eslint-disable-next-line react/no-array-index-key
          <Grid item xs={12} md={4} key={index} sx={{
            display: 'flex'
          }}>
                <SingleCard {...singlePost} />
              </Grid>)}
          </Grid>
        </Box>
      </Box>

      <Box sx={{
      backfaceVisibility: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      opacity: 1,
      ml: {
        xs: '0.4rem',
        md: '0.6rem'
      },
      textAlign: 'left',
      bottom: {
        xs: '20px',
        sm: 0
      },
      top: {
        xs: '',
        sm: '50%'
      },
      zIndex: 94034
    }}>
        <Box sx={{
        pb: '0.25rem',
        opacity: {
          xs: 0.9,
          md: 1
        }
      }}>
          <FacebookShareButton url={`lectrium.com${asPath}`} blankTarget>
            <FacebookIcon size={32} round />
          </FacebookShareButton>
        </Box>
        <Box sx={{
        pb: '0.25rem',
        opacity: {
          xs: 0.9,
          md: 1
        }
      }}>
          <LinkedinShareButton url={`lectrium.com${asPath}`} blankTarget>
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>
        </Box>
        <Box sx={{
        opacity: {
          xs: 0.9,
          md: 1
        }
      }}>
          <TwitterShareButton url={`lectrium.com${asPath}`} blankTarget>
            <TwitterIcon size={32} round />
          </TwitterShareButton>
        </Box>
      </Box>
    </>;
};
export async function getStaticPaths() {
  const {
    posts
  } = await fetch(`${env.NEXT_PUBLIC_BLOG_URL}/ghost/api/content/posts/?key=${env.NEXT_PUBLIC_CONTENT_API_KEY}`).then(response => response.json());
  const paths = posts.map((post: Post) => ({
    params: {
      slug: post.slug.toString()
    }
  }));
  return {
    paths,
    fallback: true
  };
}
async function getSinglePost(slug: string) {
  const {
    posts
  } = await fetch(`${env.NEXT_PUBLIC_BLOG_URL}/ghost/api/content/posts/slug/${slug}?key=${env.NEXT_PUBLIC_CONTENT_API_KEY}&include=tags,authors`).then(response => response.json());
  return posts[0];
}
async function getSuggestedPosts(featuredPostSlug: string) {
  const {
    posts
  } = await fetch(`${env.NEXT_PUBLIC_BLOG_URL}/ghost/api/content/posts/?key=${env.NEXT_PUBLIC_CONTENT_API_KEY}&limit=3`).then(response => response.json());
  const filteredPosts = posts.filter(({
    slug
  }: {
    slug: string;
  }) => slug !== featuredPostSlug).slice(0, 2);
  return filteredPosts;
}
export const getStaticProps = async ({
  params
}: {
  params: {
    slug: string;
  };
}) => {
  const post = (await getSinglePost(params?.slug)) || null;
  const suggestedPosts = (await getSuggestedPosts(params?.slug)) || null;
  return {
    props: {
      post,
      suggestedPosts
    }
  };
};
(Home as PageWithLayoutType).layout = Layout;
export default Home;